import { reactive } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { capitalize } from 'lodash-es';

import {
  getVerboseContactMethod,
  ContactMethod,
  type ContactMethodItem,
} from '@entities/contact-methods';

import { ValidatorsLib } from '@shared/lib';
import { validationI18n } from '@shared/i18n';
import { Analytics } from '@shared/analytics';
import { requestFormConfig } from '../config';
import { useCreateRequest } from './use-create-request';
import { RequestSource, type TourData, type UserData } from '../types';

const KZ_PHONE_COUNTRY_CODE = '+7';

export const useRequestForm = (source: RequestSource) => {
  const state = reactive<UserData>({
    name: '',
    phone: KZ_PHONE_COUNTRY_CODE,
    contact_method: ContactMethod.Call,
  });
  const createRequest = useCreateRequest();

  const contactMethods: ContactMethodItem[] = [
    {
      title: getVerboseContactMethod(ContactMethod.Call),
      value: ContactMethod.Call,
    },
    {
      title: getVerboseContactMethod(ContactMethod.WriteWhatsApp),
      value: ContactMethod.WriteWhatsApp,
    },
    {
      title: getVerboseContactMethod(ContactMethod.WriteTelegram),
      value: ContactMethod.WriteTelegram,
    },
  ];

  const onNameChange = (value: string) => {
    state.name = capitalize(value);
  };

  const onPhoneChange = (value: string) => {
    state.phone = value;
  };

  const onContactMethodChange = (value: ContactMethod) => {
    state.contact_method = value;
  };

  const { withI18nMessage } = validationI18n.model.useLocalizedValidators(
    'request-form.form.validation',
  );

  const validation$ = useVuelidate(
    {
      name: { required: withI18nMessage(required) },
      phone: {
        required: withI18nMessage(required),
        validPhone: withI18nMessage(ValidatorsLib.isCorrectPhoneNumber),
      },
      contact_method: { required: withI18nMessage(required) },
    },
    state,
  );

  const resetCreateRequest = () => createRequest.reset();

  const reset = () => {
    onNameChange('');
    onPhoneChange(KZ_PHONE_COUNTRY_CODE);
    onContactMethodChange(ContactMethod.Call);
    validation$.value.$reset();
    resetCreateRequest();
  };

  const submit = (tourData?: TourData) => {
    validation$.value.$touch();

    if (!validation$.value.$error) {
      createRequest.create({ source, user: state, ...tourData });
    } else {
      Analytics.logEvent({
        event: requestFormConfig.analyticsEvents.INVALID,
        action: validation$.value.$errors.reduce(
          (acc, { $propertyPath, $message }) => {
            if (acc[$propertyPath]) {
              acc[$propertyPath].push($message.toString());
            } else {
              acc[$propertyPath] = [$message.toString()];
            }
            return acc;
          },
          {} as Record<string, string[]>,
        ),
      });
    }
  };

  return {
    state,
    contactMethods,
    validation$,
    isLoading: createRequest.isLoading,
    isSuccess: createRequest.isSuccess,
    isError: createRequest.isError,
    onNameChange,
    onPhoneChange,
    onContactMethodChange,
    reset,
    resetCreateRequest,
    submit,
  };
};
